import { GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { FC } from 'react'
import { Stack, Typography } from '@mui/material'

import { BorderBoxWrapper } from 'components/common/BorderBoxWrapper'
import { EmailForwardning } from 'components/common/EmailForwardning'

/** @interface Props for the CCEmailsSection component. */
interface Props {
  /** Section title text. */
  title: string
  /** Section description text. */
  description: string
  /** Label displayed above the email input. */
  inputLabel: string
  /** List of CC email addresses. */
  ccEmails: string[]
  /** Whether a mutation is currently in-flight. */
  isPending: boolean
  /** Called when the user adds new emails. */
  onAdd: (emails: string[]) => void
  /** Called when the user removes an email. */
  onDelete: (email: string) => void
}

/**
 * @component Shared CC Emails section used in Workspace and Profile settings.
 * @example
 * <CCEmailsSection
 *   title="CC E-mails"
 *   description="All emails…"
 *   inputLabel="Forward the order e-mails to:"
 *   ccEmails={ccEmails}
 *   isPending={false}
 *   onAdd={(emails) => console.log(emails)}
 *   onDelete={(email) => console.log(email)}
 * />
 */
export const CCEmailsSection: FC<Props> = ({
  title,
  description,
  inputLabel,
  ccEmails,
  isPending,
  onAdd,
  onDelete,
}) => (
  <Stack gap="1.6rem">

    <Stack gap=".4rem">

      <Typography variant="text-xl" fontWeight={550} color={GRAY_900}>
        {title}
      </Typography>

      <Typography variant="text-md" fontWeight={350} color={GRAY_700}>
        {description}
      </Typography>

    </Stack>

    <BorderBoxWrapper padding="2.4rem 2.4rem .8rem 2.4rem" gap={1.6}>

      <Typography variant="text-md" fontWeight={550} color={GRAY_700}>
        {inputLabel}
      </Typography>

      <EmailForwardning
        ccEmails={ccEmails}
        disabled={isPending}
        isLoading={isPending}
        onChange={onAdd}
        onDelete={onDelete}
      />

    </BorderBoxWrapper>

  </Stack>
)
